// Piece runner: one piece at a time inside the recording session.
// Persists to localStorage so a crash/reload during a piece recovers both.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPiece, advancePiece, summarizePiece, PIECES_STORAGE_KEY, pieceCues, pieceStartCue, pieceEndCue } from '@/lib/pieces';
import { speak as speakCox } from '@/hooks/useRaceMode';

const SUMMARY_MS = 5000;

const load = (sessionKey) => {
  try {
    const raw = JSON.parse(localStorage.getItem(PIECES_STORAGE_KEY) || 'null');
    return raw && raw.sessionKey === sessionKey ? raw : null;
  } catch { return null; }
};

export default function usePieces({ sessionActive, sessionKey, elapsed, distance, strokes, strokeRate, onPieceEnd }) {
  const restored = useRef(sessionActive && sessionKey ? load(sessionKey) : null);
  const [active, setActive] = useState(restored.current?.active || null);
  const [completed, setCompleted] = useState(restored.current?.completed || []);
  const [countdown, setCountdown] = useState(null); // { def, left }
  const [summary, setSummary] = useState(null);
  const [screen, setScreen] = useState(restored.current?.active ? 2 : 1);
  const [progress, setProgress] = useState(null);

  const activeRef = useRef(active);
  const completedRef = useRef(completed);
  const snapRef = useRef({ elapsed, distance, strokes });
  snapRef.current = { elapsed, distance, strokes };
  const summaryTimer = useRef(null);
  const onEndRef = useRef(onPieceEnd);
  onEndRef.current = onPieceEnd;

  const commit = (a, c) => {
    activeRef.current = a;
    completedRef.current = c;
    setActive(a);
    setCompleted(c);
    try {
      localStorage.setItem(PIECES_STORAGE_KEY, JSON.stringify({ sessionKey, active: a, completed: c }));
    } catch { /* noop */ }
  };

  const startNow = useCallback((def) => {
    if (activeRef.current) return;
    const pc = createPiece(def, completedRef.current.length + 1, snapRef.current);
    commit(pc, completedRef.current);
    setSummary(null);
    setScreen(2);
    speakCox(pieceStartCue(pc));
  }, [sessionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback((def, mode = 'now') => {
    if (activeRef.current || !sessionActive) return;
    if (mode === 'countdown') { setCountdown({ def, left: 10 }); setScreen(2); speakCox('Piece in ten seconds.'); } else startNow(def);
  }, [sessionActive, startNow]);

  useEffect(() => {
    if (!countdown) return undefined;
    if (countdown.left <= 0) { setCountdown(null); startNow(countdown.def); return undefined; }
    if (countdown.left === 3) speakCox('Three. Two. One.');
    const id = setTimeout(() => setCountdown((c) => (c ? { ...c, left: c.left - 1 } : c)), 1000);
    return () => clearTimeout(id);
  }, [countdown, startNow]);

  // Ends the running piece; returns the full completed list (sync, for session save).
  const finish = useCallback((reason = 'manual') => {
    const pc = activeRef.current;
    if (!pc) return completedRef.current;
    const result = summarizePiece(pc, snapRef.current, reason);
    const list = [...completedRef.current, result];
    commit(null, list);
    setProgress(null);
    if (reason !== 'session_end') {
      setSummary(result);
      clearTimeout(summaryTimer.current);
      summaryTimer.current = setTimeout(() => { setSummary(null); setScreen(1); }, SUMMARY_MS);
    }
    if (reason !== 'session_end') speakCox(pieceEndCue(result));
    onEndRef.current?.(result);
    return list;
  }, [sessionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissSummary = useCallback(() => {
    clearTimeout(summaryTimer.current);
    setSummary(null);
    setScreen(1);
  }, []);

  // Live progress + auto-end
  useEffect(() => {
    const pc = activeRef.current;
    if (!pc || !sessionActive) return;
    const r = advancePiece(pc, { elapsed, distance, strokes }, strokeRate);
    if (!r.done) {
      const c = pieceCues(r.piece, r.progress, r.piece.cuesFired);
      r.piece.cuesFired = c.fired;
      if (c.cues.length) speakCox(c.cues[c.cues.length - 1]);
    }
    activeRef.current = r.piece;
    setProgress(r.progress);
    try {
      localStorage.setItem(PIECES_STORAGE_KEY, JSON.stringify({ sessionKey, active: r.piece, completed: completedRef.current }));
    } catch { /* noop */ }
    if (r.done) finish('auto');
  }, [elapsed, distance]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelCountdown = useCallback(() => { setCountdown(null); setScreen(1); }, []);

  // Clear after the session has been saved
  const clear = useCallback(() => {
    clearTimeout(summaryTimer.current);
    activeRef.current = null;
    completedRef.current = [];
    setActive(null); setCompleted([]); setSummary(null); setCountdown(null); setProgress(null); setScreen(1);
    try { localStorage.removeItem(PIECES_STORAGE_KEY); } catch { /* noop */ }
  }, []);

  useEffect(() => () => clearTimeout(summaryTimer.current), []);

  // Late recovery: session state may be restored after the first render.
  const recoveredRef = useRef(!!restored.current);
  useEffect(() => {
    if (recoveredRef.current || !sessionActive || !sessionKey || activeRef.current) return;
    recoveredRef.current = true;
    const saved = load(sessionKey);
    if (saved && (saved.active || saved.completed?.length)) {
      activeRef.current = saved.active || null;
      completedRef.current = saved.completed || [];
      setActive(activeRef.current);
      setCompleted(completedRef.current);
      if (saved.active) setScreen(2);
    }
  }, [sessionActive, sessionKey]);

  const hasScreen2 = !!(active || countdown || summary);
  return {
    active, completed, countdown, summary, progress, hasScreen2,
    screen: hasScreen2 ? screen : 1, setScreen,
    start, finish, dismissSummary, cancelCountdown, clear,
  };
}
